/**
 * Task wizard — section descriptors (single source for the UI)
 *
 * Zips the component-free section logic (`buildTaskStepLogic`) with presentation
 * (icon, i18n keys, render) into one ordered descriptor list. The container
 * builds this once and derives the section tabs + the section renderer from it.
 * Related Tasks is dropped when there are no other tasks to link to.
 */
import {
  IconInfoCircle,
  IconPaperclip,
  IconListCheck,
  IconLink,
} from '@tabler/icons-react';
import type { UseFormReturnType } from '@mantine/form';

import type { WizardStepDescriptor } from '@/components/common/wizard';
import type { WorkspaceMember } from '@/types/workspace';
import type { Attachment } from '@/types';

import { Step1BasicInfo } from '../steps/Step1BasicInfo';
import { Step2Details } from '../steps/Step2Details';
import { Step3Requirements } from '../steps/Step3Requirements';
import { Step4RelatedTasks } from '../steps/Step4RelatedTasks';
import type { GPSLocationSuggestion } from '../steps/Step1BasicInfo/Step1BasicInfo.types';
import type { MantineWizardFormData } from '../utils/mantineFormSchema';
import {
  buildTaskStepLogic,
  type WizardStepId,
} from '../utils/validation/stepValidation';

/** Prop bag passed to every step's `render`. Each step picks what it needs. */
export interface TaskWizardStepRenderProps {
  form: UseFormReturnType<MantineWizardFormData>;
  gpsLocationSuggestion: GPSLocationSuggestion;
  isSubmitting: boolean;
  existingAttachments: Attachment[];
  onDeleteExistingAttachment?: (attachmentId: string) => void;
  editingTaskId?: string | null;
  handleManualRoadInput?: (value: string) => void;
  handleRequestGPSDetection?: () => void;
  roadSource?: 'gps' | 'map' | 'manual' | null;
  isWaitingForInitialGPS?: boolean;
  members: WorkspaceMember[];
  isMembersLoading: boolean;
}

export type TaskWizardStepDescriptor = WizardStepDescriptor<
  MantineWizardFormData,
  TaskWizardStepRenderProps,
  WizardStepId
>;

type StepPresentation = Pick<
  TaskWizardStepDescriptor,
  'icon' | 'labelKey' | 'descKey' | 'render' | 'ownsScroll'
>;

const STEP_PRESENTATION: Record<WizardStepId, StepPresentation> = {
  basicInfo: {
    icon: IconInfoCircle,
    labelKey: 'taskForm.basicInfo',
    descKey: 'taskForm.basicInfoDesc',
    render: (props) => (
      <Step1BasicInfo
        form={props.form}
        gpsLocationSuggestion={props.gpsLocationSuggestion}
        isLoading={props.isSubmitting}
        handleManualRoadInput={props.handleManualRoadInput}
        handleRequestGPSDetection={props.handleRequestGPSDetection}
        roadSource={props.roadSource}
        isWaitingForInitialGPS={props.isWaitingForInitialGPS}
        members={props.members}
        isMembersLoading={props.isMembersLoading}
      />
    ),
  },
  details: {
    icon: IconPaperclip,
    labelKey: 'taskForm.attachments',
    descKey: 'taskForm.attachmentsDesc',
    ownsScroll: true,
    render: (props) => (
      <Step2Details
        form={props.form}
        existingAttachments={props.existingAttachments}
        onDeleteExistingAttachment={props.onDeleteExistingAttachment}
        isLoading={props.isSubmitting}
      />
    ),
  },
  requirements: {
    icon: IconListCheck,
    labelKey: 'taskForm.requirements',
    descKey: 'taskForm.requirementsDesc',
    ownsScroll: true,
    render: (props) => (
      <Step3Requirements form={props.form} isLoading={props.isSubmitting} />
    ),
  },
  relatedTasks: {
    icon: IconLink,
    labelKey: 'taskForm.relatedTasks',
    descKey: 'taskForm.relatedTasksDesc',
    ownsScroll: true,
    render: (props) => (
      <Step4RelatedTasks
        form={props.form}
        editingTaskId={props.editingTaskId}
      />
    ),
  },
};

/** Build the ordered descriptor list for the current wizard run. */
export function buildTaskWizardStepDescriptors(
  availableTasksCount: number
): TaskWizardStepDescriptor[] {
  return buildTaskStepLogic(availableTasksCount).map(
    (logic): TaskWizardStepDescriptor => ({
      ...logic,
      ...STEP_PRESENTATION[logic.id],
    })
  );
}
